import type { FC } from 'react';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import type {
  ExpenseSubcategory,
  ExpenseTax,
} from '@pflegenavi/frontend/api-nursing-home';
import { formatExpenseTaxLabel } from '@pflegenavi/frontend/api-nursing-home';
import {
  MoneyInput,
  TaxCodePicker,
  Typography,
} from '@pflegenavi/web-components';
import type { PartDraft } from './expensePartsDrafts';

const NO_SUBCATEGORY = '__none__';

export interface ExpensePartRowProps {
  part: PartDraft;
  subcategories: ExpenseSubcategory[];
  groupedSubcategories: Array<{
    category: string;
    items: ExpenseSubcategory[];
  }>;
  taxCodes: ExpenseTax[];
  readOnly: boolean;
  canRemove: boolean;
  onAmountChange: (amount: number) => void;
  onSubcategoryChange: (id: string | null) => void;
  onTaxCodeChange: (expenseTaxId: string | null) => void;
  onRemove: () => void;
}

export const ExpensePartRow: FC<ExpensePartRowProps> = ({
  part,
  subcategories,
  groupedSubcategories,
  taxCodes,
  readOnly,
  canRemove,
  onAmountChange,
  onSubcategoryChange,
  onTaxCodeChange,
  onRemove,
}) => {
  const { t } = useTranslation();

  const resolvedSubcategory =
    subcategories.find((s) => s.id === part.expense_subcategory_id) ?? null;
  const selectedSubcategoryId = resolvedSubcategory?.id ?? NO_SUBCATEGORY;
  const selectedIsArchived = resolvedSubcategory?.archived === true;

  // If the current selection is archived, surface it in the dropdown so the
  // user can see (and change) what's set. Otherwise it would render blank.
  const groupedForRow = useMemo(() => {
    if (!resolvedSubcategory || !selectedIsArchived) {
      return groupedSubcategories;
    }
    const archivedCat = resolvedSubcategory.category;
    const exists = groupedSubcategories.some((g) => g.category === archivedCat);
    if (exists) {
      return groupedSubcategories.map((g) =>
        g.category === archivedCat
          ? { ...g, items: [...g.items, resolvedSubcategory] }
          : g
      );
    }
    return [
      ...groupedSubcategories,
      { category: archivedCat, items: [resolvedSubcategory] },
    ];
  }, [groupedSubcategories, resolvedSubcategory, selectedIsArchived]);

  const selectedTax =
    taxCodes.find((tc) => tc.id === part.expense_tax_id) ?? null;
  const selectedTaxIsArchived = selectedTax?.archived === true;

  // Archived taxes are not offered for selection, but if the current selection
  // is archived, keep it in the dropdown so the user can see (and change) it.
  const taxCodesForRow = useMemo(() => {
    const active = taxCodes.filter((tc) => !tc.archived);
    return selectedTax?.archived ? [...active, selectedTax] : active;
  }, [taxCodes, selectedTax]);

  const taxOptionLabel = useCallback(
    (tc: ExpenseTax) =>
      tc.archived
        ? `${formatExpenseTaxLabel(tc)} (${t(
            'accounting.expenses.parts.tax-archived'
          )})`
        : formatExpenseTaxLabel(tc),
    [t]
  );

  const handleTaxCodeChange = useCallback(
    (picked: ExpenseTax | null) => {
      onTaxCodeChange(picked?.id ?? null);
    },
    [onTaxCodeChange]
  );

  return (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <Box sx={{ flex: 4, minWidth: 110 }}>
        <MoneyInput
          size="small"
          fullWidth
          label={t('accounting.expenses.parts.field.amount')}
          value={part.amount}
          onChange={(value) => onAmountChange(value ?? 0)}
          disabled={readOnly}
          allowNegative
        />
        {!readOnly && (part.amount || 0) === 0 && (
          <Typography
            variant="caption"
            color="error.main"
            sx={{ mt: 0.5, display: 'block' }}
          >
            {t('accounting.expenses.parts.zero-amount-error')}
          </Typography>
        )}
      </Box>

      <FormControl size="small" sx={{ flex: 6, minWidth: 150 }}>
        <InputLabel id={`part-subcategory-${part._internalId}-label`}>
          {t('accounting.expenses.parts.field.subcategory')}
        </InputLabel>
        <Select
          labelId={`part-subcategory-${part._internalId}-label`}
          label={t('accounting.expenses.parts.field.subcategory')}
          value={selectedSubcategoryId}
          disabled={readOnly}
          onChange={(e) => {
            const val = e.target.value;
            onSubcategoryChange(val === NO_SUBCATEGORY ? null : val);
          }}
        >
          <MenuItem value={NO_SUBCATEGORY}>
            <em>{t('accounting.expenses.parts.subcategory-none')}</em>
          </MenuItem>
          {groupedForRow.flatMap(({ category, items }) => [
            <ListSubheader key={`header-${category}`}>
              {t(`accounting.expenses.category.${category}`)}
            </ListSubheader>,
            ...items.map((sub) => (
              <MenuItem key={sub.id} value={sub.id}>
                {sub.name}
                {sub.archived
                  ? ` (${t('accounting.expenses.parts.subcategory-archived')})`
                  : ''}
              </MenuItem>
            )),
          ])}
        </Select>
        {selectedIsArchived && (
          <Typography variant="caption" color="warning.main" sx={{ mt: 0.5 }}>
            {t('accounting.expenses.parts.subcategory-archived-warning')}
          </Typography>
        )}
      </FormControl>

      <Box sx={{ flex: 5, minWidth: 130 }}>
        <TaxCodePicker
          size="small"
          fullWidth
          label={t('accounting.expenses.parts.field.tax-code')}
          taxCodes={taxCodesForRow}
          getOptionLabel={taxOptionLabel}
          value={selectedTax?.id ?? null}
          onChange={handleTaxCodeChange}
          disabled={readOnly}
          allowNone
          noneLabel={t('accounting.expenses.parts.tax-code-none')}
        />
        {selectedTaxIsArchived && (
          <Typography variant="caption" color="warning.main" sx={{ mt: 0.5 }}>
            {t('accounting.expenses.parts.tax-archived-warning')}
          </Typography>
        )}
      </Box>

      <IconButton
        size="small"
        onClick={onRemove}
        disabled={readOnly || !canRemove}
        aria-label={t('accounting.expenses.parts.remove-button')}
        sx={{ width: 40, height: 40 }}
      >
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>
    </Stack>
  );
};
